import type { SnapshotRegenerationStatusResponse } from "@repo/shared/types";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { ApiError } from "@/lib/api/_client";

const POLL_INTERVAL_MS = 2000;

/**
 * How long a watch may run: the API's own window for a queued rebuild
 * (REGENERATION_REQUEST_TTL_MS), past which it stops reporting "regenerating"
 * and the stale banner offers Regenerate again.
 */
const WATCH_LIMIT_MS = 10 * 60 * 1000;

/** What a watch that gives up reports: still stale, nothing running — the banner's Regenerate state. */
const GAVE_UP: SnapshotRegenerationStatusResponse = {
  needsSnapshotRegeneration: true,
  snapshotsRegenerating: false,
};

/**
 * QL-675 — follow a snapshot rebuild running in the API's worker.
 *
 * `regenerating` turning true starts a watch: the instance's snapshot status is
 * polled every two seconds until a status fetched AFTER the watch started says
 * the rebuild is over, and then `onFinished` is called with it — once. The
 * caller refreshes what the rebuild rewrote (every snapshot view and the
 * completion) and sets its own copy of the state from the status, WITHOUT a
 * read that queues a rebuild: when `needsSnapshotRegeneration` is still true
 * the rebuild failed, and the stale banner offers Regenerate again.
 *
 * The watch is latched. Once started, it ends on that poll, even if the
 * caller's own copy says "not regenerating" first (a metadata refetch that
 * landed after a small rebuild finished): the poll is what triggers the
 * refresh. A new watch needs `regenerating` to turn true again.
 *
 * It gives up instead of polling forever when the status read answers 4xx
 * (the instance was deleted, the session ended) or the watch outlives the
 * API's 10-minute window. Then `onFinished` gets a "still stale, nothing
 * running" status, so the banner offers Regenerate again — what the API
 * reports past that window anyway.
 *
 * Shared by the advisor wizard and the public flow; each passes its own key
 * and fetcher. The status is never cached (`gcTime: 0`), and an answer or an
 * error older than the watch is ignored: one from an earlier rebuild must not
 * end this one.
 *
 * Returns whether a watch is running, so the "updating" notice can show from
 * the moment the rebuild is known to be queued.
 */
export function useSnapshotRegenerationWatch(opts: {
  queryKey: readonly unknown[];
  regenerating: boolean;
  fetchStatus: () => Promise<SnapshotRegenerationStatusResponse>;
  onFinished: (status: SnapshotRegenerationStatusResponse) => void;
}): { watching: boolean } {
  const { queryKey, regenerating, fetchStatus, onFinished } = opts;

  // When the running watch started; null when none is.
  const [watchStartedAt, setWatchStartedAt] = useState<number | null>(() =>
    regenerating ? Date.now() : null,
  );
  useEffect(() => {
    if (regenerating) setWatchStartedAt((started) => started ?? Date.now());
  }, [regenerating]);

  const watching = watchStartedAt !== null;
  const { data, dataUpdatedAt, error, errorUpdatedAt } = useQuery({
    queryKey,
    queryFn: fetchStatus,
    enabled: watching,
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: 0,
    gcTime: 0,
  });

  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;
  useEffect(() => {
    if (watchStartedAt === null) return;
    const finish = (status: SnapshotRegenerationStatusResponse) => {
      setWatchStartedAt(null);
      onFinishedRef.current(status);
    };
    // Like the data, an error older than the watch belongs to an earlier one.
    const clientError =
      error instanceof ApiError &&
      error.status >= 400 &&
      error.status < 500 &&
      errorUpdatedAt >= watchStartedAt;
    if (clientError) {
      finish(GAVE_UP);
      return;
    }
    if (data && dataUpdatedAt >= watchStartedAt && !data.snapshotsRegenerating) {
      finish(data);
      return;
    }
    // Checked on every poll (the interval keeps them coming while it runs).
    if (Date.now() - watchStartedAt > WATCH_LIMIT_MS) finish(GAVE_UP);
  }, [watchStartedAt, data, dataUpdatedAt, error, errorUpdatedAt]);

  return { watching };
}
